// @core/stores/billing/BillingStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { billingApi } from '@core/api';
import type {
  Account,
  CreateAccountRequest,
  CreateJournalEntryRequest,
  CreateJournalEntryResponse,
  GetJournalEntryResponse,
  ListJournalEntriesResponse,
  ListLedgerEntriesResponse,
  PostJournalEntryRequest,
  PostJournalEntryResponse,
  UpdateAccountRequest,
  VoidJournalEntryRequest,
  VoidJournalEntryResponse,
} from '@core/types/billing';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class BillingStore {
  // List state
  items: Account[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Account | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Account>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Account | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Account | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create account in Chart of Accounts
   */
  async CreateAccount(data: CreateAccountRequest): Promise<Account> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.CreateAccount(data);
      const item = response.account;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create journal entry
   */
  async CreateJournalEntry(data: CreateJournalEntryRequest): Promise<CreateJournalEntryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.CreateJournalEntry(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get account by ID
   */
  async GetAccount(id: string): Promise<Account> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await billingApi.GetAccount(id);
      const item = response.account;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get account balance
   */
  async GetAccountBalance(id: string): Promise<Account> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await billingApi.GetAccountBalance(id);
      const item = response.account;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get journal entry by ID
   */
  async GetJournalEntry(id: string): Promise<GetJournalEntryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.GetJournalEntry(id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List accounts (Chart of Accounts)
   */
  async ListAccounts(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await billingApi.ListAccounts(params);

      runInAction(() => {
        this.items = response.accounts?.accounts ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List journal entries
   */
  async ListJournalEntries(params?: PaginatedRequest): Promise<ListJournalEntriesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.ListJournalEntries(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List ledger entries
   */
  async ListLedgerEntries(params?: PaginatedRequest): Promise<ListLedgerEntriesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.ListLedgerEntries(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Post journal entry to ledger
   */
  async PostJournalEntry(id: string, data: PostJournalEntryRequest): Promise<PostJournalEntryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.PostJournalEntry(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update account
   */
  async UpdateAccount(id: string, data: UpdateAccountRequest): Promise<Account> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.UpdateAccount(id, data);
      const item = response.account;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Void journal entry (creates reversing entry)
   */
  async VoidJournalEntry(id: string, data: VoidJournalEntryRequest): Promise<VoidJournalEntryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.VoidJournalEntry(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const billingStore = new BillingStore();
